import { ApolloClient, ApolloProvider, from, InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from "@apollo/client/link/error";
import { Lottie } from '@crello/react-lottie';
import createUploadLink from "apollo-upload-client/createUploadLink.mjs";
import { lazy, Suspense, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

// Custom Component
import RecoveryCodeModal from './components/common/modal/user/recoverycode';

// Scroll Component
import ScrollToTop from './components/common/scrolltotop';

// Auth-Service
import PrivateRoute from './utils/privateroute';

// Utility Service
import { lazyRetry } from './utils';
import { removeUserSession } from './utils/auth';

// Settings
import { animationOptions } from './settings';

// Redux-Functions
import { selectRecoveryCodeModal, selectTheme, selectToken, setToken, setUser } from './redux/userSlice';

// Page
const Payment = lazy(() => lazyRetry(() => import('./pages/payment')));
const PaymentList = lazy(() => lazyRetry(() => import('./pages/payment/list')));
const PaymentEdit = lazy(() => lazyRetry(() => import('./pages/payment/edit')));
const PaymentUpdate = lazy(() => lazyRetry(() => import('./modules/user/settings/Payment/update/Update')));
const PaymentRetry = lazy(() => lazyRetry(() => import('./pages/payment/retry')));
// const PaymentSummary = lazy(() => lazyRetry(() => import('./pages/payment/summary')));
const PaymentSummary = lazy(() => lazyRetry(() => import('./modules/user/settings/Payment/summary/Summary')));
const PaymentSuccess = lazy(() => lazyRetry(() => import('./pages/payment/success')));
const PaymentFailed = lazy(() => lazyRetry(() => import('./pages/payment/failed')));
// const UserSettings = lazy(() => lazyRetry(() => import('./pages/user/settings')));
const UserSettings = lazy(() =>
  lazyRetry(() => import("./modules/user/settings/Settings"))
);
// const SubscriptionSummary = lazy(() => lazyRetry(() => import('./pages/subscription/summary')));
const SubscriptionSummary = lazy(() => lazyRetry(() => import('./modules/user/settings/components/Subscription/components/Summary')));
// const SubscriptionUpgrade = lazy(() => lazyRetry(() => import('./pages/subscription/upgrade')));
const SubscriptionUpgrade = lazy(() => lazyRetry(() => import('./modules/user/settings/components/Subscription/components/Upgrade')));
const SubscriptionAddonsAdd = lazy(() => lazyRetry(() => import('./pages/subscription/addons/add')));
const SubscriptionAddonsRemove = lazy(() => lazyRetry(() => import('./pages/subscription/addons/remove')));
const Expired = lazy(() => lazyRetry(() => import('./pages/expired')));
const FourZeroFour = lazy(() => lazyRetry(() => import('./pages/error/404')));

const App = () => {
    const navigate = useNavigate(); 
    const location = useLocation();

    const dispatch = useDispatch();
    const token = useSelector(selectToken);
    const theme = useSelector(selectTheme);
    const recoveryCodeModal = useSelector(selectRecoveryCodeModal);

    const options = animationOptions(theme);

    useEffect(() => {
        if (document.getElementsByTagName("body") && theme === 'dark') {
            document.getElementsByTagName("body")[0].setAttribute("data-bs-theme", "dark")
        } else if (document.getElementsByTagName("body") && theme === 'light') {
            document.getElementsByTagName("body")[0].setAttribute("data-bs-theme", "light")
        }
    }, [theme])

    useEffect(() => {
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.ready.then((registration) => {
                registration.update();
            });
        }
    }, [location])

    const authLink = setContext((request, previousContext) => {
        if (token) {
            return {
                headers: {
                    ...previousContext.headers,
                    Authorization: `Bearer ${token}`,
                    'apollo-require-preflight': 'true'
                }
            };
        } else {
            return {
                headers: {
                    ...previousContext.headers,
                    'apollo-require-preflight': 'true'
                }
            };
        }
    })

    const uploadLink = createUploadLink({
        uri: process.env.REACT_APP_API_URL
    });

    const errorLink = onError(({ graphQLErrors }) => {
        if (graphQLErrors?.length > 0 && token) {
            if (graphQLErrors?.some(item => item?.message === 'Invalid payment token' || item?.message === 'Forbidden resource')) {
                navigate('/expired')
            }
            if (graphQLErrors?.some(item => item?.message === 'Invalid secure token')) {
                handleLogout()
            }
        }
    })

    const cache = new InMemoryCache({})

    const client = new ApolloClient({
        link: from([errorLink, authLink.concat(uploadLink)]),
        cache,
        connectToDevTools: true
    });

    /**
        * @function handleLogout
        * @params
        * @description used to logout the user
    */
    const handleLogout = async () => {
        client.clearStore()
        removeUserSession()
        dispatch(setUser())
        dispatch(setToken())
        const mainDomain = process.env.REACT_APP_MAIN_DOMAIN?.replace(/\/$/, "");
        window.location.href = `${mainDomain}/user/logout`
    }

    return <ApolloProvider client={client}>
        <ScrollToTop />
        <Suspense fallback={<>
            <div className='d-flex justify-content-center align-items-center overflow-hidden vw-100 vh-100'>
                <Lottie config={options} height={350} width={350} />
            </div>
        </>}>
            <Routes>
                {/* Private Route */}
                <Route element={<PrivateRoute />}>
                    {/* Payment Page Route */}
                    <Route path="/payment" element={<Payment />} />
                    {/* Payment List Page Route */}
                    <Route path="/payment-list" element={<PaymentList />} />
                    {/* Payment Edit Page Route */}
                    <Route path="/payment-edit" element={<PaymentEdit />} />
                    {/* Payment Update Page Route */}
                    <Route path="/payment-update" element={<PaymentUpdate />} />
                    {/* Payment Retry Page Route */}
                    <Route path="/payment-retry" element={<PaymentRetry />} />
                    {/* Payment Summary Page Route */}
                    <Route path="/payment-summary" element={<PaymentSummary />} />
                    {/* Payment Success Page Route */}
                    <Route path="/payment-success" element={<PaymentSuccess />} />
                    {/* User Settings Page Route */}
                    <Route path="/settings" element={<UserSettings />} />
                    {/* Subscription Summary Page Route */}
                    <Route path="/subscription-summary" element={<SubscriptionSummary />} />
                    {/* Subscription Upgrade Page Route */}
                    <Route path="/subscription-upgrade" element={<SubscriptionUpgrade />} />
                    {/* Subscription Addons Add Page Route */}
                    <Route path="/subscription-addons-add" element={<SubscriptionAddonsAdd />} />
                    {/* Subscription Addons Remove Page Route */}
                    <Route path="/subscription-addons-remove" element={<SubscriptionAddonsRemove />} />
                </Route>
                {/* Payment Failed Page Route */}
                <Route path="/payment-failed" element={<PaymentFailed />} />

                {/* 404 Page Route */}
                <Route path="/expired" element={<Expired />} />
                {/* 404 Page Route */}
                <Route path="/404" element={<FourZeroFour />} />
                {/* Error Page Route */}
                <Route path="*" element={<Navigate to="/404" replace />} />
            </Routes>
        </Suspense>

        {/* Recovery Code Modal */}
        <RecoveryCodeModal
            show={recoveryCodeModal}
        />
    </ApolloProvider>
}

export default App;
